import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Paperclip, RefreshCw } from "lucide-react";
import api from "../lib/api";

const STATUSES = ["NEW", "CONTACTED", "CLOSED"];
const BADGE = { NEW: "bg-amber-100 text-amber-800", CONTACTED: "bg-sky-100 text-sky-800", CLOSED: "bg-slate-100 text-slate-600" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function Inquiries() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load inquiries"));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <MessageSquare size={22} className="text-[var(--pg-accent)]" />
          <div>
            <h1 className="font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Website Inquiries</h1>
            <p className="text-[13px] text-slate-500">Short-form messages from the Contact page and homepage form. Detailed requests appear under RFQs.</p>
          </div>
        </div>
        <button data-testid="inquiries-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-[13px] font-[Poppins] font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)]"><RefreshCw size={14} /> Refresh</button>
      </div>
      {!items ? <div className="mt-6 text-slate-400 text-[14px]">Loading…</div> : items.length === 0 ? (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-[14px]" data-testid="inquiries-empty">No inquiries yet.</div>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((i) => (
            <div key={i.id} data-testid={`inquiry-row-${i.reference || i.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)]">{i.reference || "INQ (legacy)"}</span>
                    <span className={`text-[10.5px] font-[Poppins] font-bold px-2 py-0.5 rounded-full ${BADGE[i.status] || BADGE.CLOSED}`}>{i.status || "NEW"}</span>
                    {i.buyer_type && <span className="text-[11px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{i.buyer_type}</span>}
                  </div>
                  <div className="mt-1 text-[13.5px] text-slate-600">
                    <strong className="text-[var(--pg-navy)]">{i.name}</strong>{i.company ? ` · ${i.company}` : ""} · <a href={`mailto:${i.email}`} className="text-[var(--pg-accent)]">{i.email}</a>{i.phone ? ` · ${i.phone}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[12px] text-slate-400">{fmt(i.created_at)}</span>
                  <select data-testid={`inquiry-status-${i.reference || i.id}`} value={i.status || "NEW"} onChange={(e) => setStatus(i.id, e.target.value)} className="pg-input !py-1.5 !px-2 text-[12.5px] w-auto">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <p className="mt-3 text-[14px] text-[var(--pg-navy)] whitespace-pre-wrap leading-relaxed">{i.message}</p>
              {(i.delivery_location || i.deadline) && (
                <div className="mt-2 text-[12.5px] text-slate-500">{i.delivery_location && <>Deliver to: <strong>{i.delivery_location}</strong></>}{i.delivery_location && i.deadline && " · "}{i.deadline && <>Needed by: <strong>{i.deadline}</strong></>}</div>
              )}
              {i.documents?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {i.documents.map((d) => (
                    <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/${d.id}/download`} target="_blank" rel="noopener noreferrer"
                      data-testid={`inquiry-doc-${d.id}`} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-[var(--pg-bg)] border border-slate-200 rounded-lg px-2.5 py-1.5 text-[var(--pg-navy)] hover:border-[var(--pg-accent)]">
                      <Paperclip size={13} /> {d.original_filename}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
